import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  FlatList,
  useWindowDimensions,
  type PressableStateCallbackType,
} from "react-native";

type HoverableState = PressableStateCallbackType & { hovered?: boolean };
import { GradebookCell } from "./GradebookCell";
import type { GradebookStudent, GradeCell } from "./GradebookCell";
import {
  GradebookActivityType,
  StudentActivityStatus,
  type GetGradebookQuery,
  type GradebookActivityColumn,
} from "@axio/graphql/generated/graphql";
import { LucideArrowUpDown, LucideChevronUp, LucideChevronDown } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import type { DraftWeightingConfig } from "./gradebookWeighting.types";
import { calculateCumulativeTotal } from "./gradebookWeighting.utils";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";

const ROW_HEIGHT = 44;

function getTotalColor(pct: number): string {
  if (pct >= 90) return "#3B7C0F";
  if (pct >= 80) return "#101828";
  if (pct >= 70) return "#B54708";
  if (pct >= 60) return "#B91C1C";
  return "#DC2626";
}

function calcColumnAverage(students: GradebookStudent[], activityId: string): number | null {
  const scores = students
    .map((s) => s.grades[activityId]?.percentageScore)
    .filter((s): s is number => s !== null && s !== undefined);
  if (!scores.length) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

function calcClassAverage(students: GradebookStudent[]): number | null {
  if (!students.length) return null;
  const totals = students.map((s) => s.percentageScore ?? 0);
  return Math.round(totals.reduce((a, b) => a + b, 0) / students.length);
}

function Avatar({ initials }: { initials: string; name: string }) {
  return (
    <View style={styles.avatarCircle}>
      <CustomText
        style={styles.avatarText}
        text={initials.slice(0, 2).toUpperCase()}
        weight="bold"
        size="xs"
        useTranslationText={false}
      />
    </View>
  );
}

function SortIcon({ active, dir, hovered }: { active: boolean; dir: "asc" | "desc"; hovered?: boolean }) {
  if (active) {
    return dir === "asc" ? (
      <LucideChevronUp size={12} color={"#6B7280"} />
    ) : (
      <LucideChevronDown size={12} color={"#6B7280"} />
    );
  }
  if (hovered) return <LucideArrowUpDown size={9} color={"#6B7280"} />;
  return null;
}

const ACTIVITY_TYPE_LABEL: Record<GradebookActivityType, string> = {
  [GradebookActivityType.Assessment]: "Assessment",
  [GradebookActivityType.Assignment]: "Assignment",
  [GradebookActivityType.Project]: "Project",
};

function TypePill({ type }: { type: GradebookActivityType }) {
  return (
    <CustomText
      style={styles.typePillText}
      text={ACTIVITY_TYPE_LABEL[type] ?? type}
      size="xxs"
      useTranslationText={false}
    />
  );
}

interface SortState {
  column: string | null;
  dir: "asc" | "desc";
}

export type GradebookMatrixData = NonNullable<NonNullable<GetGradebookQuery["getGradebook"]>["data"]>;

type DisplayStudent = GradebookStudent & { weightedScore?: number | null };

export interface GradebookMatrixProps {
  data: GradebookMatrixData | null | undefined;
  weightingConfig?: DraftWeightingConfig | null;
  onOpenGradingSheet?: (studentId: string, activityId: string, activityType: GradebookActivityType) => void;
  onMessageStudent?: (student: GradebookStudent) => void;
  onStudentPress?: (student: GradebookStudent) => void;
  onMaxPointsChange?: (activityId: string, activityType: GradebookActivityType, maxPoints: number) => void;
  onCellStatusAction?: (
    studentId: string,
    activityId: string,
    submissionId: string | null | undefined,
    activityType: GradebookActivityType,
    status: StudentActivityStatus
  ) => void;
}

export function GradebookMatrix({
  data,
  weightingConfig,
  onOpenGradingSheet,
  onMessageStudent,
  onStudentPress,
  onMaxPointsChange,
  onCellStatusAction,
}: GradebookMatrixProps) {
  const { assignments, students: initialStudents } = useMemo(() => {
    if (!data) return { assignments: [] as GradebookActivityColumn[], students: [] as GradebookStudent[] };

    const assignmentsDerived: GradebookActivityColumn[] = data.columns;

    const studentsDerived: GradebookStudent[] = data.rows
      .filter((row) => !!row.student)
      .map((row) => {
        const grades: Record<string, GradeCell> = {};
        row.cells.forEach((cell) => {
          grades[cell.activityId] = cell;
        });
        return {
          student: row.student,
          percentageScore: row.percentageScore,
          grades,
        };
      });

    return { assignments: assignmentsDerived, students: studentsDerived };
  }, [data]);

  const [assignmentData, setAssignmentData] = useState<GradebookActivityColumn[]>(assignments);
  const [students, setStudents] = useState<GradebookStudent[]>(initialStudents);
  const [sort, setSort] = useState<SortState>({ column: null, dir: "asc" });
  const [editingMaxPts, setEditingMaxPts] = useState<string | null>(null);
  const [editMaxValue, setEditMaxValue] = useState("");
  const [undoToast, setUndoToast] = useState<{ id: string; oldMax: number; newMax: number } | null>(null);
  // Use a ref instead of state so scroll sync doesn't trigger re-renders on every scroll event
  const hOffsetRef = useRef(0);

  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerScrollRef = useRef<ScrollView>(null);
  const maxScrollRef = useRef<ScrollView>(null);
  const footerScrollRef = useRef<ScrollView>(null);
  const rowScrollersRef = useRef<Map<string, ScrollView>>(new Map());

  useEffect(() => {
    setAssignmentData(assignments);
    setStudents(initialStudents);
  }, [assignments, initialStudents]);

  const displayStudents = useMemo((): DisplayStudent[] => {
    if (!weightingConfig?.enabled) return students;

    return students.map((student) => {
      const weightedScore = calculateCumulativeTotal(
        assignmentData.map((assignment) => {
          const cell = student.grades[assignment.activityId];
          return {
            activityId: assignment.activityId,
            activityType: assignment.activityType,
            percentageScore: cell?.percentageScore,
            status: cell?.status,
            maxPoints: assignment.maxPoints,
          };
        }),
        weightingConfig
      );
      return { ...student, weightedScore };
    });
  }, [assignmentData, students, weightingConfig]);

  const { height: windowHeight } = useWindowDimensions();
  // Approximate height of all fixed UI above the student list:
  // admin header(72) + page padding(32) + grading center header(~88) + tab nav(~68) + filters(~72) + legend(~62)
  // + matrix header row(58) + max row(34) + footer(44) + bottom padding(32)
  const studentListHeight = Math.max(windowHeight - 562, 300);

  const sorted = useMemo(() => {
    return [...displayStudents].sort((a, b) => {
      if (!sort.column) return 0;
      let va: string | number, vb: string | number;
      if (sort.column === "student") {
        va = a.student.name;
        vb = b.student.name;
      } else if (sort.column === "total") {
        va = a.percentageScore ?? -1;
        vb = b.percentageScore ?? -1;
      } else if (sort.column === "weighted_total") {
        va = a.weightedScore ?? -1;
        vb = b.weightedScore ?? -1;
      } else {
        va = a.grades[sort.column]?.percentageScore ?? -1;
        vb = b.grades[sort.column]?.percentageScore ?? -1;
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [displayStudents, sort]);

  const classAvg = useMemo(() => calcClassAverage(sorted), [sorted]);

  const weightedClassAvg = useMemo(() => {
    if (!weightingConfig?.enabled) return null;
    const scores = sorted.map((s) => s.weightedScore).filter((s): s is number => s != null);
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [sorted, weightingConfig]);

  const handleSort = (col: string) => {
    setSort((prev) => ({ column: col, dir: prev.column === col && prev.dir === "asc" ? "desc" : "asc" }));
  };

  const handleCellStatusChange = (studentId: string, activityId: string, action: StudentActivityStatus) => {
    const submissionId = students.find((s) => s.student.id === studentId)?.grades[activityId]?.submissionId;
    const activityType = assignmentData.find((a) => a.activityId === activityId)?.activityType;
    if (activityType) {
      onCellStatusAction?.(studentId, activityId, submissionId, activityType, action);
    }
  };

  const saveMaxPoints = () => {
    if (!editingMaxPts) return;

    const parsedValue = Number(editMaxValue.trim());
    if (Number.isFinite(parsedValue) && parsedValue > 0) {
      const activity = assignmentData.find((a) => a.activityId === editingMaxPts);
      const old = activity?.maxPoints ?? parsedValue;
      setAssignmentData((prev) =>
        prev.map((a) => (a.activityId === editingMaxPts ? { ...a, maxPoints: parsedValue } : a))
      );
      if (undoTimer.current) clearTimeout(undoTimer.current);
      setUndoToast({ id: editingMaxPts, oldMax: old, newMax: parsedValue });
      undoTimer.current = setTimeout(() => setUndoToast(null), 4000);
      if (activity) {
        onMaxPointsChange?.(editingMaxPts, activity.activityType, parsedValue);
      }
    }
    setEditingMaxPts(null);
  };

  const undoMaxPoints = () => {
    if (!undoToast) return;
    const activity = assignmentData.find((a) => a.activityId === undoToast.id);
    setAssignmentData((prev) =>
      prev.map((a) => (a.activityId === undoToast.id ? { ...a, maxPoints: undoToast.oldMax } : a))
    );
    if (activity) {
      onMaxPointsChange?.(undoToast.id, activity.activityType, undoToast.oldMax);
    }
    setUndoToast(null);
  };

  const syncTo = useCallback((x: number, source: ScrollView | null) => {
    hOffsetRef.current = x;
    const apply = (sv: ScrollView | null) => {
      if (sv && sv !== source) sv.scrollTo({ x, animated: false });
    };
    apply(headerScrollRef.current);
    apply(maxScrollRef.current);
    apply(footerScrollRef.current);
    rowScrollersRef.current.forEach(apply);
  }, []);

  const setRowScrollerRef = useCallback(
    (studentId: string) => (node: ScrollView | null) => {
      if (node) {
        rowScrollersRef.current.set(studentId, node);
        // Sync newly mounted rows to the current horizontal scroll offset
        const offset = hOffsetRef.current;
        if (offset > 0) {
          requestAnimationFrame(() => node.scrollTo({ x: offset, animated: false }));
        }
      } else {
        rowScrollersRef.current.delete(studentId);
      }
    },
    []
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Pressable style={[styles.studentColCell, styles.headerHeight]} onPress={() => handleSort("student")}>
          {(state) => {
            const { hovered } = state as HoverableState;
            return (
              <>
                <CustomText style={styles.headerText} text="STUDENT" size="xs" weight="bold" />
                <SortIcon active={sort.column === "student"} dir={sort.dir} hovered={hovered} />
              </>
            );
          }}
        </Pressable>

        <ScrollView
          ref={headerScrollRef}
          horizontal
          onScroll={(e) => syncTo(e.nativeEvent.contentOffset.x, headerScrollRef.current)}
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
          style={styles.flex1}>
          {assignmentData.map((a) => (
            <Pressable
              key={a.activityId}
              style={[styles.assignmentColCell, styles.headerHeight]}
              onPress={() => handleSort(a.activityId)}>
              {(state) => {
                const { hovered } = state as HoverableState;
                return (
                  <>
                    <View style={styles.colTitleRow}>
                      <CustomText
                        style={styles.headerText}
                        numberOfLines={1}
                        text={a.activityName}
                        size="xs"
                        weight="medium"
                        useTranslationText={false}
                      />
                      <SortIcon active={sort.column === a.activityId} dir={sort.dir} hovered={hovered} />
                    </View>
                    <TypePill type={a.activityType} />
                  </>
                );
              }}
            </Pressable>
          ))}
        </ScrollView>

        {weightingConfig?.enabled && (
          <Pressable
            style={[styles.weightedTotalColCell, styles.headerHeight]}
            onPress={() => handleSort("weighted_total")}>
            {(state) => {
              const { hovered } = state as HoverableState;
              return (
                <>
                  <CustomText
                    style={[styles.headerText, { textAlign: "center", width: "100%" }]}
                    text="Weighted Total"
                    size="xs"
                    weight="bold"
                  />
                  <SortIcon active={sort.column === "weighted_total"} dir={sort.dir} hovered={hovered} />
                </>
              );
            }}
          </Pressable>
        )}

        <Pressable style={[styles.totalColCell, styles.headerHeight]} onPress={() => handleSort("total")}>
          {(state) => {
            const { hovered } = state as HoverableState;
            return (
              <View style={styles.colTitleRow}>
                <CustomText style={styles.headerText} text="TOTAL" size="xs" weight="bold" />
                <SortIcon active={sort.column === "total"} dir={sort.dir} hovered={hovered} />
              </View>
            );
          }}
        </Pressable>
      </View>

      <View style={styles.maxRow}>
        <View style={[styles.studentColCell, styles.maxHeight]}>
          <CustomText style={styles.maxLabel} text="Max Pts" size="xs" />
        </View>

        <ScrollView
          ref={maxScrollRef}
          horizontal
          onScroll={(e) => syncTo(e.nativeEvent.contentOffset.x, maxScrollRef.current)}
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
          style={styles.flex1}>
          {assignmentData.map((a) => (
            <TouchableOpacity
              key={a.activityId}
              style={[styles.assignmentMaxCell, editingMaxPts === a.activityId && styles.editingCell]}
              onPress={() => {
                setEditingMaxPts(a.activityId);
                setEditMaxValue(String(a.maxPoints));
              }}>
              {editingMaxPts === a.activityId ? (
                <CustomTextInput
                  style={styles.maxInput}
                  value={editMaxValue}
                  onChangeText={setEditMaxValue}
                  onBlur={saveMaxPoints}
                  onSubmitEditing={saveMaxPoints}
                  keyboardType="decimal-pad"
                  autoFocus
                  selectTextOnFocus
                  placeholder={""}
                />
              ) : (
                <CustomText style={styles.maxText} text={`/ ${a.maxPoints} pts`} size="xs" useTranslationText={false} />
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>

        {weightingConfig?.enabled && <View style={[styles.weightedTotalColCell, styles.maxHeight]} />}
        <View style={[styles.totalColCell, styles.maxHeight]} />
      </View>

      <View style={[styles.studentListArea, { height: studentListHeight }]}>
        <FlatList
          data={sorted}
          keyExtractor={(s) => s.student.id}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled
          style={styles.flex1}
          getItemLayout={(_data, index) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index })}
          initialNumToRender={20}
          maxToRenderPerBatch={10}
          windowSize={5}
          updateCellsBatchingPeriod={50}
          renderItem={({ item: student, index: rowIdx }) => (
            <View style={styles.row}>
              <View
                style={[styles.studentColCell, styles.rowHeight, rowIdx % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <Avatar initials={student.student.initials} name={student.student.name} />
                <Pressable
                  onPress={() => (onStudentPress ?? onMessageStudent)?.(student)}
                  accessibilityRole="link"
                  accessibilityLabel={`Open profile for ${student.student.name}`}
                  style={styles.flex1}>
                  {(state) => {
                    const { hovered } = state as HoverableState;
                    return (
                      <CustomText
                        style={[styles.studentNameText, hovered && styles.studentNameTextHovered]}
                        numberOfLines={1}
                        text={student.student.name}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                      />
                    );
                  }}
                </Pressable>
              </View>

              <ScrollView
                ref={setRowScrollerRef(student.student.id)}
                horizontal
                onScroll={(e) =>
                  syncTo(e.nativeEvent.contentOffset.x, rowScrollersRef.current.get(student.student.id) ?? null)
                }
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator={false}
                contentOffset={{ x: hOffsetRef.current, y: 0 }}
                style={styles.flex1}>
                {assignmentData.map((a) => {
                  const cell: GradeCell = student.grades[a.activityId] ?? {
                    __typename: "GradebookCell",
                    activityId: a.activityId,
                    activityName: a.activityName,
                    activityType: a.activityType,
                    moduleId: a.moduleId,
                    moduleName: a.moduleName,
                    percentageScore: null,
                    status: null,
                    submissionId: null,
                    submittedOn: null,
                    isRetake: false,
                    isLate: false,
                    dueDate: a.dueDate ?? null,
                  };
                  return (
                    <GradebookCell
                      key={a.activityId}
                      student={student}
                      assignment={a}
                      cell={cell}
                      rowEven={rowIdx % 2 === 0}
                      onOpenSheet={onOpenGradingSheet}
                      onCellStatusChange={handleCellStatusChange}
                    />
                  );
                })}
              </ScrollView>

              {weightingConfig?.enabled && (
                <View
                  style={[
                    styles.weightedTotalColCell,
                    styles.rowHeight,
                    rowIdx % 2 === 0 ? styles.rowEven : styles.rowOdd,
                  ]}>
                  <CustomText
                    style={[
                      styles.totalText,
                      { color: student.weightedScore != null ? getTotalColor(student.weightedScore) : "#9CA3AF" },
                    ]}
                    text={student.weightedScore != null ? `${student.weightedScore}%` : "–"}
                    size="xs"
                    useTranslationText={false}
                  />
                </View>
              )}

              <View style={[styles.totalColCell, styles.rowHeight, rowIdx % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <CustomText
                  style={[
                    styles.totalText,
                    {
                      color: student.percentageScore != null ? getTotalColor(student.percentageScore) : "#9CA3AF",
                    },
                  ]}
                  text={student.percentageScore != null ? `${student.percentageScore}%` : "–"}
                  size="xs"
                  useTranslationText={false}
                />
              </View>
            </View>
          )}
        />
      </View>

      <View style={styles.footer}>
        <View style={[styles.studentColCell, styles.footerHeight]}>
          <CustomText style={styles.headerText} text="AVG" size="xs" weight="bold" />
        </View>

        <ScrollView
          ref={footerScrollRef}
          horizontal
          onScroll={(e) => syncTo(e.nativeEvent.contentOffset.x, footerScrollRef.current)}
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator
          style={styles.flex1}>
          {assignmentData.map((a) => {
            const avg = calcColumnAverage(sorted, a.activityId);
            return (
              <View key={a.activityId} style={styles.avgCell}>
                {avg !== null ? (
                  <CustomText
                    style={[styles.avgScore, { color: getTotalColor(avg) }]}
                    text={`${avg}`}
                    size="xs"
                    useTranslationText={false}
                  />
                ) : (
                  <CustomText style={styles.avgEmpty} text="–" size="xs" useTranslationText={false} />
                )}
              </View>
            );
          })}
        </ScrollView>

        {weightingConfig?.enabled && (
          <View style={[styles.weightedTotalColCell, styles.footerHeight]}>
            <CustomText
              style={[
                styles.avgScore,
                { color: weightedClassAvg !== null ? getTotalColor(weightedClassAvg) : "#9CA3AF" },
              ]}
              text={weightedClassAvg !== null ? `${weightedClassAvg}%` : "–"}
              size="xs"
              useTranslationText={false}
            />
          </View>
        )}

        <View style={[styles.totalColCell, styles.footerHeight]}>
          <CustomText
            style={[styles.avgScore, { color: classAvg !== null ? getTotalColor(classAvg) : "#9CA3AF" }]}
            text={classAvg !== null ? `${classAvg}%` : "–"}
            size="xs"
            useTranslationText={false}
          />
        </View>
      </View>

      {undoToast && (
        <View style={styles.toast}>
          <CustomText
            style={styles.toastText}
            text={`Max points → ${undoToast.newMax}`}
            size="xs"
            useTranslationText={false}
          />
          <TouchableOpacity onPress={undoMaxPoints}>
            <CustomText style={styles.undoText} text="↩ Undo" size="xs" useTranslationText={false} />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },

  flex1: { flex: 1 },

  avatarCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#CCD7ff",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: { color: "#3459D4", fontSize: 11, letterSpacing: 0.3 },

  typePillText: { color: "#475467" },

  headerText: { color: "#344054" },
  studentNameText: { color: "#101828" },
  studentNameTextHovered: { textDecorationLine: "underline", color: "#3459D4" },

  headerHeight: { height: 58 },
  maxHeight: { height: 34 },
  rowHeight: { height: 44 },
  footerHeight: { height: 44 },

  studentColCell: {
    width: 345,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    gap: 10,
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
  },

  assignmentColCell: {
    width: 144,
    justifyContent: "center",
    paddingHorizontal: 12,
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    gap: 3,
  },

  totalColCell: {
    width: 72,
    alignItems: "center",
    justifyContent: "center",
    borderLeftWidth: 1,
    borderLeftColor: "#E5E7EB",
    backgroundColor: "#F3F4F6",
  },

  weightedTotalColCell: {
    width: 88,
    alignItems: "center",
    justifyContent: "center",
    borderLeftWidth: 1,
    borderLeftColor: "#E5E7EB",
    backgroundColor: "#EEF2FF",
  },

  headerRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
  },
  colTitleRow: { flexDirection: "row", alignItems: "center" },

  maxRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  assignmentMaxCell: {
    width: 144,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
  },
  editingCell: {
    backgroundColor: "#EFF6FF",
  },
  maxLabel: { color: "#344054" },
  maxText: { fontSize: 12, color: "#6B7280", fontVariant: ["tabular-nums"] },
  maxInput: {
    width: "100%",
    textAlign: "center",
    fontSize: 12,
    fontWeight: "500",
    color: "#344054",
    padding: 0,
    borderWidth: 0,
  },
  row: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#E5E7EB" },
  rowEven: { backgroundColor: "#FFFFFF" },
  rowOdd: { backgroundColor: "#F9FAFB" },

  totalText: {
    fontSize: 13,
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
  },

  footer: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  avgCell: {
    width: 144,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
  },
  avgScore: {
    fontSize: 13,
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
  },
  avgEmpty: {
    fontSize: 14,
    color: "#9CA3AF",
  },
  toast: {
    position: "absolute",
    bottom: 16,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: "#111827",
    borderRadius: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 8,
  },
  toastText: { fontSize: 13, color: "#FFFFFF" },
  undoText: { fontSize: 13, fontWeight: "600", color: "#BFDBFE" },
  studentListArea: {
    overflow: "hidden",
  },
});
